import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router'
// Imported as a URL, not inlined: the SVG carries `id` attributes
// (Background, Text) that would collide if this markup were ever reused
// more than once on a page.
import eslLogoUrl from '../assets/esl-logo.svg'
import './AppFrame.css'

/**
 * The authenticated app frame: header, primary nav, content well.
 *
 * Deliberately thin. Joe supplies the full design system when 2.2 starts, so
 * this exists to give routes somewhere to render and to fix the page rhythm —
 * not to pre-commit component APIs that 2.2 would then have to unpick.
 */
export function AppFrame({
  children,
  accountLabel,
  onSignOut,
}: {
  children: ReactNode
  accountLabel?: string | undefined
  onSignOut?: (() => void) | undefined
}) {
  return (
    <div className="esl-frame">
      <header className="esl-frame__header">
        <div className="esl-frame__header-inner">
          <span className="esl-frame__brand">
            {/* alt="" — decorative. The adjacent "ESL Vision" text already
                carries identification; the lockup's own VISION line is
                illegible at this size (an accepted trade, not a defect), so
                the text stays as the thing that actually reads. */}
            <img src={eslLogoUrl} alt="" className="esl-frame__brand-mark" />
            ESL Vision
          </span>
          {accountLabel ? (
            <span className="esl-frame__account" title={accountLabel}>
              {accountLabel}
            </span>
          ) : null}
          {onSignOut ? (
            <button type="button" className="esl-btn esl-btn--quiet" onClick={onSignOut}>
              Sign out
            </button>
          ) : null}
        </div>
      </header>

      <nav className="esl-frame__nav" aria-label="Primary">
        <div className="esl-frame__nav-inner">
          <NavLink to="/" className="esl-frame__nav-link" end>
            Orders
          </NavLink>
          <NavLink to="/products" className="esl-frame__nav-link">
            Products
          </NavLink>
          <FeedbackLink />
        </div>
      </nav>

      <main className="esl-frame__main" id="main">
        <div className="esl-frame__main-inner">{children}</div>
      </main>
    </div>
  )
}

const FEEDBACK_TO = 'joe.poisson@eslvision.com'
const FEEDBACK_SUBJECT = 'Rep dashboard feedback'

/**
 * "Send feedback": a plain mailto, no form and no storage (task 2026-11-02).
 * The body is pre-filled with the page the rep is on. useLocation() makes
 * the link re-render on every route change, so the URL is always current.
 */
function FeedbackLink() {
  useLocation()
  const page = typeof window === 'undefined' ? '' : window.location.href
  const href =
    `mailto:${FEEDBACK_TO}` +
    `?subject=${encodeURIComponent(FEEDBACK_SUBJECT)}` +
    `&body=${encodeURIComponent(`Page: ${page}\n\n`)}`
  return (
    <a href={href} className="esl-frame__nav-link esl-frame__feedback">
      Send feedback
    </a>
  )
}

/**
 * Centred single-column frame for the screens that exist outside the
 * authenticated shell: sign-in, callback, not-authorized.
 */
export function CenteredFrame({ children }: { children: ReactNode }) {
  return (
    <div className="esl-centered">
      <div className="esl-centered__card">{children}</div>
    </div>
  )
}
